import { useState, type FormEvent } from 'react';
import { ApiError } from '@/api/client';
import { useNoteActions } from '@/api/hooks';
import type { AgentInfo, PlayerNote, WhaleRow } from '@/api/types';
import { formatDateTime } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Button } from '@/ui/Button';
import { Modal } from '@/ui/Modal';

// Support agents' notes on a player: a marker in the whales row (hover to read, click to open) and a
// dialog to read, add, edit and delete them. Everyone sees every note; the author edits their own,
// the author or an admin deletes. The server enforces the same rule; the buttons only follow it.

export const NOTE_MAX_LENGTH = 2000;

const noteLine = (note: PlayerNote) =>
  `${note.agentId} · ${formatDateTime(note.createdAt)}${note.updatedAt ? ' (edited)' : ''}\n${note.body}`;

function BubbleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
      <path d="M3 4.5A2.5 2.5 0 0 1 5.5 2h9A2.5 2.5 0 0 1 17 4.5v6a2.5 2.5 0 0 1-2.5 2.5H9l-3.6 3.1A.75.75 0 0 1 4.2 15.5V13A2.5 2.5 0 0 1 3 10.5v-6Z" />
    </svg>
  );
}

// Hovering shows every note (author, time, text); clicking opens the dialog.
export function NoteMarker({ row, onOpen }: { row: WhaleRow; onOpen(): void }) {
  // A middleware from before player notes sends rows without `notes` (and would answer 404 to a
  // write): no marker until it is updated, rather than a whales page that fails to render.
  if (!Array.isArray(row.notes)) return null;
  const count = row.notes.length;
  const playerId = toPlayerId(row.playerUuid);
  return (
    <button
      type="button"
      onClick={onOpen}
      title={count ? row.notes.map(noteLine).join('\n\n') : 'Add a note'}
      aria-label={count ? `Notes for ${playerId} (${count})` : `Add a note for ${playerId}`}
      className={
        count
          ? 'ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-200'
          : 'ml-2 inline-flex items-center rounded px-1 py-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500'
      }
    >
      <BubbleIcon />
      {count > 0 && <span>{count}</span>}
    </button>
  );
}

function errorText(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 403)
    return 'Only the author can change a note; an admin can delete it.';
  if (error instanceof ApiError && error.status === 404) return 'That note was already deleted.';
  return error instanceof Error ? error.message : String(error);
}

const TEXTAREA_CLASS =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function NoteItem({ note, agent }: { note: PlayerNote; agent: AgentInfo }) {
  const { update, remove } = useNoteActions();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [confirming, setConfirming] = useState(false);
  const mine = note.agentId === agent.agentId;
  const error = errorText(update.error ?? remove.error);

  const save = (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    update.mutate({ id: note.id, body }, { onSuccess: () => setEditing(false) });
  };

  return (
    <li className="rounded-md border border-slate-200 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>
          <span className="font-semibold text-slate-700">{note.agentId}</span> · {formatDateTime(note.createdAt)}
          {note.updatedAt && <span title={`Edited ${formatDateTime(note.updatedAt)}`}> (edited)</span>}
        </span>
        {!editing && (
          <span className="flex gap-2">
            {mine && (
              <button type="button" className="text-blue-700 hover:underline" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
            {(mine || agent.admin) &&
              (confirming ? (
                <>
                  <button
                    type="button"
                    className="font-semibold text-red-700 hover:underline"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(note.id)}
                  >
                    Confirm delete
                  </button>
                  <button type="button" className="text-slate-600 hover:underline" onClick={() => setConfirming(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="text-red-700 hover:underline" onClick={() => setConfirming(true)}>
                  Delete
                </button>
              ))}
          </span>
        )}
      </div>
      {editing ? (
        <form onSubmit={save} className="mt-2 space-y-2">
          <textarea
            aria-label="Edit note"
            className={TEXTAREA_CLASS}
            rows={3}
            maxLength={NOTE_MAX_LENGTH}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={!draft.trim() || update.isPending}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setDraft(note.body);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{note.body}</p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </li>
  );
}

export function NotesDialog({ row, agent, onClose }: { row: WhaleRow; agent: AgentInfo; onClose(): void }) {
  const { create } = useNoteActions();
  const [draft, setDraft] = useState('');
  const playerId = toPlayerId(row.playerUuid);
  const error = errorText(create.error);

  const add = (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    create.mutate({ playerUuid: row.playerUuid, body }, { onSuccess: () => setDraft('') });
  };

  return (
    <Modal title={`Notes · ${playerId}`} onClose={onClose}>
      <form onSubmit={add} className="space-y-2">
        <textarea
          aria-label="New note"
          className={TEXTAREA_CLASS}
          rows={3}
          maxLength={NOTE_MAX_LENGTH}
          placeholder="Write a note about this player"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <p className="text-xs text-slate-500">
          Every support agent sees this note. Do not write document numbers, card details or health information.
        </p>
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <Button type="submit" disabled={!draft.trim() || create.isPending}>
          Add note
        </Button>
      </form>
      <ul className="mt-4 space-y-2">
        {row.notes.length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
        {row.notes.map((note) => (
          <NoteItem key={note.id} note={note} agent={agent} />
        ))}
      </ul>
    </Modal>
  );
}
